import { useLocation, useNavigate } from 'react-router-dom';
import { useShellContext } from '@/app/shell/AppShell';
import { RouteRecoveryScreen } from '@/app/states/RouteStates';
import { useSpendByMerchant } from '@/api/hooks/useSpendByMerchant';
import { SpendAggregationSurface } from './SpendAggregationSurface';
import { ViewSwitcher } from './ViewSwitcher';
import { DateScopeGuard } from '@/app/shell/DateScopeGuard';
import { hrefForSpendDrilldown } from './transactionDrillIn';

function MerchantsPageContent() {
  const {
    accountIds,
    dateFrom: scopeFrom,
    dateTo: scopeTo,
    scopeAvailability,
    retryScopeAvailability,
  } = useShellContext();
  const location = useLocation();
  const navigate = useNavigate();
  const scopeReady = scopeAvailability === 'ready';
  const spend = useSpendByMerchant({
    accountIds: accountIds ?? [],
    dateFrom: scopeFrom,
    dateTo: scopeTo,
    enabled: scopeReady,
  });

  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (isOffline) return <RouteRecoveryScreen kind="offline" frame="inline" />;

  if (!scopeReady) {
    return (
      <div>
        <p role="status" className="mt-4 rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          {scopeAvailability === 'pending'
            ? 'Checking transaction scope availability…'
            : (
              <>
                Transaction scope availability could not be checked.{' '}
                <button type="button" className="min-h-11 rounded-md border border-input px-3 underline" onClick={retryScopeAvailability}>
                  Retry
                </button>
              </>
            )}
        </p>
      </div>
    );
  }

  return (
    <SpendAggregationSurface
      mode="merchant"
      spend={spend}
      onDrilldown={(descriptor) => {
        navigate(hrefForSpendDrilldown(location.search, location.hash, descriptor));
      }}
    />
  );
}

export function MerchantsPage() {
  const { dateScopeAvailability } = useShellContext();
  return (
    <section aria-labelledby="by-merchant-heading" className="mx-auto w-full max-w-5xl p-4">
      <h1 id="by-merchant-heading" tabIndex={-1} className="text-title font-semibold">Transactions</h1>
      <ViewSwitcher />
      <DateScopeGuard availability={dateScopeAvailability}>
        <MerchantsPageContent />
      </DateScopeGuard>
    </section>
  );
}

export default MerchantsPage;
